import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, catchError, defer, EMPTY, expand, timer, finalize } from 'rxjs';
import { TopupService } from '../services/topup.service';
import { AddTokenService } from '../services/add-token.service';
import { TransactionService } from '../services/transaction.service';
import { WalletDialogFlow } from './wallet-dialog-flow';
import type {
  InitTopupResponse,
  PostTxStatus,
  TopupAuthTxnResult,
  TopupCategory,
  TopupDialogStep,
  TopupFee,
  TopupMethod,
  TopupToken,
} from '../models/topup.model';
import type { SimpleBodyItem, StatusBodyItem } from '../models/payment.model';
import type { AddTokenFormValue } from '../models/add-token.model';

// ── Store ──────────────────────────────────────────────────────────────────

/**
 * TopupStore
 *
 * Scoped store for the "Recharger portefeuille" dialog.
 * Provided at TopupDialogComponent level via `providers: [TopupStore]`.
 *
 * Each "loading" action is driven by a Subject that triggers a switchMap
 * pipeline — this ensures in-flight requests are automatically cancelled
 * when a new action is dispatched (e.g. user changes the amount quickly).
 *
 * Dialog steps controlled by TopupStore.dialogStep():
 *   'form' → Progressive-reveal form (amount → methods → tokens → details)
 *
 * There is no PIN step: a topup never asks for one. The UI step machine lives in
 * TopupModal; this store only exposes the async state it drives.
 *
 * On successful submit, lastTopupResult() is set and the dialog component
 * closes itself — the success banner is rendered on the parent wallet page.
 *
 * Architecture: Component → Store → Service → Api → Backend
 */
@Injectable()
export class TopupStore {
  private static readonly PENDING_STATUSES = new Set([0, 1, 2, 6]);
  private static readonly DETAIL_MAX_POLLS = 3;
  private static readonly DETAIL_POLL_MS = 5_000;
  private readonly topupService = inject(TopupService);
  private readonly addTokenService = inject(AddTokenService);
  private readonly transactionService = inject(TransactionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly flow = new WalletDialogFlow(this.destroyRef);

  // ── Mutable private state ─────────────────────────────────────────────────

  private readonly _amount = signal<number | null>(null);
  private readonly _selectedMethod = signal<TopupMethod | null>(null);
  private readonly _selectedToken = signal<TopupToken | null>(null);
  private readonly _categoryId = signal<string>('');
  private readonly _message = signal<string>('');
  private readonly _dialogStep = signal<TopupDialogStep>('form');
  private readonly _addAccountMode = signal(false);

  private readonly _methodsLoading = signal(false);
  private readonly _tokensLoading = signal(false);
  private readonly _authTxnLoading = signal(false);
  private readonly _submitting = signal(false);
  private readonly _addTokenLoading = signal(false);
  private readonly _addTokenError = signal<string | null>(null);
  private readonly _error = signal<string | null>(null);
  // Per-pipeline error signals — each covers exactly one async operation.
  // _error is reserved for submit failures only so the error step can display it.
  private readonly _methodsError = signal<string | null>(null);
  private readonly _tokensError = signal<string | null>(null);
  private readonly _authTxnError = signal<string | null>(null);

  private readonly _methods = signal<TopupMethod[]>([]);
  private readonly _tokens = signal<TopupToken[]>([]);
  private readonly _categories = signal<TopupCategory[]>([]);
  private readonly _authTxnResult = signal<TopupAuthTxnResult | null>(null);
  private readonly _lastTopupResult = signal<InitTopupResponse | null>(null);
  private readonly _txDetailLoading = signal(false);
  private readonly _txStatus = signal<PostTxStatus | null>(null);
  private readonly _balanceFromDetail = signal<string | null>(null);
  private readonly _referenceFromDetail = signal<string | null>(null);

  // ── Public readonly state ─────────────────────────────────────────────────

  readonly amount = this._amount.asReadonly();
  readonly selectedMethod = this._selectedMethod.asReadonly();
  readonly selectedToken = this._selectedToken.asReadonly();
  readonly categoryId = this._categoryId.asReadonly();
  readonly message = this._message.asReadonly();
  readonly dialogStep = this._dialogStep.asReadonly();
  readonly addAccountMode = this._addAccountMode.asReadonly();
  readonly methodsLoading = this._methodsLoading.asReadonly();
  readonly tokensLoading = this._tokensLoading.asReadonly();
  readonly authTxnLoading = this._authTxnLoading.asReadonly();
  readonly submitting = this._submitting.asReadonly();
  readonly addTokenLoading = this._addTokenLoading.asReadonly();
  readonly addTokenError = this._addTokenError.asReadonly();
  readonly error = this._error.asReadonly();
  readonly methodsError = this._methodsError.asReadonly();
  readonly tokensError = this._tokensError.asReadonly();
  readonly authTxnError = this._authTxnError.asReadonly();
  readonly methods = this._methods.asReadonly();
  readonly tokens = this._tokens.asReadonly();
  readonly categories = this._categories.asReadonly();
  readonly authTxnResult = this._authTxnResult.asReadonly();
  readonly lastTopupResult = this._lastTopupResult.asReadonly();
  readonly txDetailLoading = this._txDetailLoading.asReadonly();
  readonly txStatus = this._txStatus.asReadonly();
  readonly balanceFromDetail = this._balanceFromDetail.asReadonly();
  /** Canonical transaction reference (reference_no) from the post-topup detail —
   *  matches what history/detail/receipt show. Null until the detail arrives. */
  readonly referenceFromDetail = this._referenceFromDetail.asReadonly();

  // ── Computed ──────────────────────────────────────────────────────────────

  readonly isAmountValid = computed(() => {
    const a = this._amount();
    return a !== null && a > 0;
  });

  /** Inline validation error for the amount field */
  readonly amountError = computed((): string | null => {
    const a = this._amount();
    if (a === null || a === 0) return null;
    if (a < 0) return 'Le montant doit être positif.';
    return null;
  });

  /** Progressive reveal: show methods section once amount is valid */
  readonly showMethods = computed(() => {
    const a = this._amount();
    return a !== null && a > 0;
  });

  /** Progressive reveal: show token/account section once a method is selected */
  readonly showTokens = computed(() => this._selectedMethod() !== null);

  /** Progressive reveal: show category + message once a token is selected */
  readonly showDetails = computed(() => this._selectedToken() !== null);

  /** Enable "Continuer" only when all required fields are filled and auth_txn has resolved */
  readonly canContinue = computed(
    () =>
      this.isAmountValid() &&
      this._selectedMethod() !== null &&
      this._selectedToken() !== null &&
      !this._authTxnLoading() &&
      this._authTxnResult() !== null,
  );

  /**
   * True when the selected method requires card fields (card_number, expiry, cvv)
   * rather than a phone number. Used to switch the inline add-account form.
   *
   * The web API sometimes returns processor: null for card methods even when cards
   * exist (mobile API does return the processor). Fall back to name matching.
   */
  readonly isCardMethod = computed(() => {
    const method = this._selectedMethod();
    if (!method) return false;
    if (method.processor) {
      return method.processor.top_up_required_params.some(
        (p) => p.name === 'card_number' || p.name === 'pan',
      );
    }
    // Fallback: the web API may omit the processor object for card methods
    return method.name === 'Carte';
  });

  /** Total amount = base amount + fee (from auth_txn if available, else method fee) */
  readonly totalWithFees = computed(() => {
    const base = this._amount() ?? 0;
    const fee = this._authTxnResult()?.fee.creditor ?? this._selectedMethod()?.fee ?? 0;
    return base + fee;
  });

  /** Currently selected category object */
  readonly selectedCategory = computed(
    () => this._categories().find((c) => c.id === this._categoryId()) ?? null,
  );

  // ── Subjects (cancellable pipelines) ──────────────────────────────────────

  private readonly methodsTrigger$ = new Subject<number>();
  private readonly tokensTrigger$ = new Subject<string>();
  private readonly authTxnTrigger$ = new Subject<{
    amount: number;
    processorId: string;
    tokenId: string;
    methodId: string;
  }>();
  private readonly submitTrigger$ = new Subject<{
    amount: number;
    processorId: string;
    tokenId: string;
    categoryId: string;
    fees: { fee: TopupFee; commission: TopupFee };
    message: string;
    pin?: string;
  }>();

  private readonly addTokenTrigger$ = new Subject<AddTokenFormValue>();
  private readonly _detailTrigger$ = new Subject<string>();

  constructor() {
    this.wireMethodsPipeline();
    this.wireTokensPipeline();
    this.wireAuthTxnPipeline();
    this.wireSubmitPipeline();
    this.wireAddTokenPipeline();
    this.wireDetailPipeline();
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  setAmount(value: number | null): void {
    this._amount.set(value);
    // Reset downstream selections when amount changes
    this._selectedMethod.set(null);
    this._selectedToken.set(null);
    this._methods.set([]);
    this._tokens.set([]);
    this._addAccountMode.set(false);
    this._authTxnResult.set(null);
    this._categories.set([]);
    this._addTokenLoading.set(false);
    this._addTokenError.set(null);
    this._methodsError.set(null);
    this._tokensError.set(null);
    this._authTxnError.set(null);
    this._error.set(null);

    if (value !== null && value > 0) {
      this._methodsLoading.set(true);
      this.methodsTrigger$.next(value);
    }
  }

  selectMethod(method: TopupMethod): void {
    if (this._selectedMethod()?.id === method.id) return;
    this._selectedMethod.set(method);
    this._selectedToken.set(null);
    this._addAccountMode.set(false);
    this._authTxnResult.set(null);
    this._categories.set([]);
    this._addTokenLoading.set(false);
    this._addTokenError.set(null);
    this._tokensError.set(null);
    this._authTxnError.set(null);
    this._error.set(null);

    if (method.processor) {
      this._tokensLoading.set(true);
      this._tokens.set([]);
      this.tokensTrigger$.next(method.processor._id);
    } else {
      // Card methods may have no processor on the web API response.
      // Tokens cannot be fetched without a processor ID — the user must
      // add a card manually via the inline add-account form.
      this._tokens.set([]);
      this._tokensLoading.set(false);
    }
  }

  retryTokens(): void {
    const method = this._selectedMethod();
    if (!method?.processor?._id) return;
    this._tokensError.set(null);
    this._tokensLoading.set(true);
    this._tokens.set([]);
    this.tokensTrigger$.next(method.processor._id);
  }

  selectToken(token: TopupToken): void {
    this._selectedToken.set(token);
    this._addAccountMode.set(false);
    this._authTxnError.set(null);
    this._error.set(null);

    const method = this._selectedMethod();
    const amount = this._amount();
    if (!method?.processor || amount === null) return;

    this._authTxnLoading.set(true);
    this._authTxnResult.set(null);
    this.authTxnTrigger$.next({
      amount,
      processorId: method.processor._id,
      tokenId: token._id,
      methodId: method.id,
    });
  }

  toggleAddAccount(): void {
    this._addAccountMode.update((v) => !v);
    // Clear add-token error when toggling the form
    this._addTokenError.set(null);
  }

  /**
   * Submit the "Ajouter un compte" form.
   * On success: the new token is prepended to the list, selected automatically,
   * and the add-account form is closed.
   */
  addToken(form: AddTokenFormValue): void {
    if (this._addTokenLoading()) return;
    this._addTokenLoading.set(true);
    this._addTokenError.set(null);
    this.addTokenTrigger$.next(form);
  }

  setCategoryId(id: string): void {
    this._categoryId.set(id);
  }

  setMessage(msg: string): void {
    this._message.set(msg);
  }

  /**
   * "Continuer" — triggered by the footer CTA.
   * Skips the separate summary step (summary is shown inline in the form).
   * Submits immediately.
   *
   * Topup carries no PIN: `auth_txn.is_pin_required` is deliberately NOT consulted
   * here. The PIN-gated flows are send + withdrawal, which own a dedicated keypad
   * step. Branching on the flag used to park this store on a `'pin'` sub-state that
   * no component renders, which stranded the dialog on an infinite spinner and
   * never issued the request.
   */
  continuer(): void {
    if (!this.canContinue()) return;
    this.dispatchSubmit();
  }

  /**
   * Submit with a PIN. Currently has NO caller: the topup flow has no keypad step
   * and `continuer()` never routes to one. Kept only until the backend confirms it
   * accepts a PIN-less `init_psp_topup`; drop it (and the `pin` plumbing down through
   * TopupService/TopupApi/InitTopupRequest) once that is verified. Do not re-wire
   * `continuer()` through here — that is what caused the infinite-spinner bug.
   */
  onPinEntered(pin: string): void {
    this.dispatchSubmit(pin);
  }

  backToForm(): void {
    this._dialogStep.set('form');
  }

  /** Reset all state — call on dialog close */
  reset(): void {
    this._amount.set(null);
    this._selectedMethod.set(null);
    this._selectedToken.set(null);
    this._categoryId.set('');
    this._message.set('');
    this._dialogStep.set('form');
    this._addAccountMode.set(false);
    this._methodsLoading.set(false);
    this._tokensLoading.set(false);
    this._authTxnLoading.set(false);
    this._submitting.set(false);
    this._addTokenLoading.set(false);
    this._addTokenError.set(null);
    this._methodsError.set(null);
    this._tokensError.set(null);
    this._authTxnError.set(null);
    this._error.set(null);
    this._methods.set([]);
    this._tokens.set([]);
    this._categories.set([]);
    this._authTxnResult.set(null);
    this._lastTopupResult.set(null);
    this._balanceFromDetail.set(null);
    this._referenceFromDetail.set(null);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private dispatchSubmit(pin?: string): void {
    const method = this._selectedMethod();
    const token = this._selectedToken();
    const amount = this._amount();
    const authTxn = this._authTxnResult();
    // authTxn is guaranteed non-null here: canContinue() requires !authTxnLoading(),
    // which only clears after a successful auth_txn response sets _authTxnResult.
    if (!method?.processor || !token || amount === null || !authTxn) return;

    this._error.set(null); // clear previous submit error before each attempt
    this._submitting.set(true);
    this.submitTrigger$.next({
      amount,
      processorId: method.processor._id,
      tokenId: token._id,
      categoryId: this._categoryId(),
      fees: {
        fee: authTxn.fee,
        commission: authTxn.commission,
      },
      message: this._message(),
      pin,
    });
  }

  // ── Pipelines ─────────────────────────────────────────────────────────────

  private wireMethodsPipeline(): void {
    this.flow.wire(this.methodsTrigger$, (amount) => this.topupService.getMethods(amount), {
      next: (methods) => {
        // Keep "Carte" last (copy, not in-place sort, to avoid mutating the response).
        const sorted = [...methods].sort((a, b) => {
          if (a.name === 'Carte') return 1;
          if (b.name === 'Carte') return -1;
          return 0;
        });
        this._methods.set(sorted);
        this._methodsLoading.set(false);
      },
      error: (err) => {
        this._methodsError.set((err as Error).message);
        this._methodsLoading.set(false);
      },
    });
  }

  private wireTokensPipeline(): void {
    this.flow.wire(this.tokensTrigger$, (processorId) => this.topupService.getTokens(processorId), {
      next: (tokens) => {
        this._tokens.set(tokens);
        this._tokensLoading.set(false);
      },
      error: (err) => {
        this._tokensError.set((err as Error).message);
        this._tokensLoading.set(false);
      },
    });
  }

  private wireAuthTxnPipeline(): void {
    this.authTxnTrigger$
      .pipe(
        // defer: a synchronous throw from the service/API layer would otherwise escape this
        // projection, bypass the catchError below and kill the subscription permanently.
        switchMap(({ amount, processorId, tokenId, methodId }) =>
          defer(() => this.topupService.authTxn(amount, processorId, tokenId, methodId)).pipe(
            catchError((err: Error) => {
              this._authTxnError.set(err.message);
              this._authTxnLoading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this._authTxnResult.set(result);
        this._categories.set(result.categories);
        // Category is intentionally NOT pre-selected — the user decides.
        this._authTxnLoading.set(false);
      });
  }

  private wireSubmitPipeline(): void {
    this.submitTrigger$
      .pipe(
        // defer: see wireAuthTxnPipeline — keeps a synchronous throw catchable so the
        // dialog can never strand on its "Recharge en cours…" spinner.
        switchMap(({ amount, processorId, tokenId, categoryId, fees, message, pin }) =>
          defer(() =>
            this.topupService.initTopup(
              amount,
              processorId,
              tokenId,
              categoryId,
              fees,
              message,
              pin,
            ),
          ).pipe(
            catchError((err: Error) => {
              this._error.set(err.message);
              this._submitting.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this._submitting.set(false);
        this._lastTopupResult.set(result);
        this._txDetailLoading.set(true);
        this._txStatus.set(null);
        this._balanceFromDetail.set(null);
        this._referenceFromDetail.set(null);
        this._detailTrigger$.next(result.payment_id);
      });
  }

  private wireDetailPipeline(): void {
    this._detailTrigger$
      .pipe(
        switchMap((paymentId) => {
          const fetch$ = () =>
            this.transactionService.getTransactionDetail(paymentId).pipe(catchError(() => EMPTY));

          return fetch$().pipe(
            expand((detail, attemptIndex) => {
              const hasBalance = detail.response.transaction_body
                .filter((item): item is SimpleBodyItem => item.type === 'simple')
                .flatMap((item) => item.metadata)
                .some((m) => m.value_type === 'balance');

              const statusValue =
                detail.response.transaction_body.find(
                  (item): item is StatusBodyItem => item.type === 'status',
                )?.metadata[0]?.status_value ?? -1;

              const shouldRetry =
                !hasBalance &&
                TopupStore.PENDING_STATUSES.has(statusValue) &&
                attemptIndex < TopupStore.DETAIL_MAX_POLLS;

              return shouldRetry ? timer(TopupStore.DETAIL_POLL_MS).pipe(switchMap(fetch$)) : EMPTY;
            }),
            // Safety net: if the FIRST fetch fails the stream completes without
            // ever emitting (`getTransactionDetail` swallows errors into EMPTY),
            // so `subscribe` never runs and the flag would stay stuck `true`.
            // On the happy path the first emission below already cleared it.
            finalize(() => this._txDetailLoading.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        const statusMeta = detail.response.transaction_body.find(
          (item): item is StatusBodyItem => item.type === 'status',
        )?.metadata[0];
        this._txStatus.set(
          statusMeta
            ? { statusValue: statusMeta.status_value, statusLabel: statusMeta.display_value }
            : null,
        );

        const balanceMeta = detail.response.transaction_body
          .filter((item): item is SimpleBodyItem => item.type === 'simple')
          .flatMap((item) => item.metadata)
          .find((m) => m.value_type === 'balance');
        this._balanceFromDetail.set(balanceMeta?.display_value ?? null);

        // Canonical reference (reference_no) — same field the transaction
        // detail/receipt use, so the top-up confirmation matches history.
        const reference = detail.response.transaction_header?.[0]?.metadata?.reference_no;
        this._referenceFromDetail.set(reference ?? null);

        this._txDetailLoading.set(false);
      });
  }

  private wireAddTokenPipeline(): void {
    this.flow.wire(
      this.addTokenTrigger$,
      (form) => {
        const method = this._selectedMethod();
        if (!method) {
          this._addTokenError.set('Aucune méthode sélectionnée.');
          this._addTokenLoading.set(false);
          return EMPTY;
        }
        // tokenService = 1 → TOP_UP tokens only
        return this.addTokenService.createToken(form, method, 1);
      },
      {
        next: (newToken) => {
          // Prepend the new token to the list and auto-select it
          this._tokens.update((prev) => [newToken, ...prev]);
          this._addTokenLoading.set(false);
          this._addAccountMode.set(false);
          this.selectToken(newToken);
        },
        error: (err) => {
          this._addTokenError.set((err as Error).message);
          this._addTokenLoading.set(false);
        },
      },
    );
  }
}
